import moment from 'moment';
import type { DateType, ObjectId } from './types';

export function toISOString (
  date: DateType | null | undefined
): string | null {
  if (typeof date === 'string') return date;
  if (date instanceof Date    ) return date.toISOString();
  if (moment.isMoment(date)   ) return date.toISOString();
  return null;
}


/**
 * Check if value is a Mongoose ObjectId.
 */
export function isMongooseObjectId (value: unknown): value is ObjectId {
  if ( value === null
    || typeof value !== 'object'
    || !('_bsontype' in value)
    || value._bsontype !== 'ObjectId'
    || typeof value.toString !== 'function'
  ) return false;
  return true;
}
// export function isMongooseObjectId (value: unknown): value is MongooseTypes.ObjectId {
//   return value instanceof MongooseTypes.ObjectId;
// }

export type Range = {
  start: moment.Moment;
  end:   moment.Moment;
};

export namespace range {
  export type Type = Range;

  /**
   * Create a date range.
   */
  export function create (start: DateType, end: DateType): Range {
    return {
      start: moment(start),
      end:   moment(end  ),
    };
  }

  /**
   * Clone a date range.
   */
  export function clone (range: Range): Range {
    return {
      start: range.start.clone(),
      end:   range.end  .clone(),
    };
  }

  /**
   * Check if a date is contained within a date range.
   */
  export function contains (range: Range, date: moment.Moment) {
    return !date.isBefore(range.start) && !date.isAfter(range.end);
  }

  /**
   * Check if two date ranges overlap.
   */
  export function overlaps (a: Range, b: Range): boolean;
  /**
   * Checks if there is any overlap between two arrays of date ranges.
   */
  export function overlaps (a: Range[], b: Range[]): boolean;
  export function overlaps (a: Range | Range[], b: Range | Range[]) {
    if (Array.isArray(a) && Array.isArray(b)) {
      return a.some(x => b.some(y => overlaps(x, y)));
    }
    if (!Array.isArray(a) && !Array.isArray(b)) {
      return !(a.end.isBefore(b.start) || a.start.isAfter(b.end));
    }
    throw new Error('Invalid arguments for range.overlaps');
  }



  /**
   * Check if two date ranges are equal.
   */
  export function isEqual (a: Range, b: Range) {
    return a.start.isSame(b.start) && a.end.isSame(b.end);
  }

  /**
   * Get intersection of two ranges, or null if they do not overlap.
   */
  export function intersect (a: Range, b: Range): Range | null {
    const start = moment.max(a.start, b.start);
    const end   = moment.min(a.end,   b.end  );
    if (start.isAfter(end)) return null;
    return { start, end };
  }
}